if (!customElements.get("sticky-header")) {
  customElements.define(
    "sticky-header",
    class StickyHeader extends HTMLElement {
      constructor() {
        super();

        this.stickyType = this.getAttribute("data-sticky-type") || "none";
        if (this.stickyType === "none") return;

        this.categoryMenu = document.getElementById("Header-Menu-Categories");
        this._DIRECTION_DEADZONE = 8;
        this._MIN_UP_DURATION = 200;

        this._onScrollRaw = this.onScroll.bind(this);
        this._boundSetHeaderHeight = this.setHeaderHeight.bind(this);
        this._scrollTicking = false;
        this._headerActionRaf = 0;
        this._pendingHeaderAction = "";
        this._lastDirectionAttr = "";
        this._observer = null;
        this._mediaQueryList = null;
        this.onScrollHandler = () => {
          if (!this._scrollTicking) {
            this._scrollTicking = true;
            requestAnimationFrame(() => {
              this._onScrollRaw();
              this._scrollTicking = false;
            });
          }
        };
        this.preventHide = false;
        this.preventReveal = false;
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.preScrollTop = 0;
        this.preStatus = 0;
        this._confirmedDirection = "down";
        this._lastDirectionChangeY = 0;
        this._upFlipTime = 0;
        this._upFlipScrollTop = 0;
        this._revealDebounce = null;
      }

      scheduleHeaderAction(action) {
        if (this._pendingHeaderAction === action) return;
        this._pendingHeaderAction = action;
        if (this._headerActionRaf) cancelAnimationFrame(this._headerActionRaf);
        this._headerActionRaf = requestAnimationFrame(() => {
          this._headerActionRaf = 0;
          const next = this._pendingHeaderAction;
          this._pendingHeaderAction = "";
          if (next === "hide") this.hide();
          else if (next === "reveal") this.reveal();
          else if (next === "reset") this.reset();
        });
      }

      connectedCallback() {
        this.header = document.querySelector(".section-header");
        this._isMobileProductPage =
          window.matchMedia("(max-width: 749px)").matches &&
          !!(document.body && document.body.classList.contains("template-product"));
        this.headerIsAlwaysSticky =
          this.stickyType === "always" ||
          this.stickyType === "reduce-logo-size";
        this.headerBounds = {};

        this.setHeaderHeight();

        this._mediaQueryList = window.matchMedia("(min-width: 750px)");
        this._mediaQueryList.addEventListener("change", this._boundSetHeaderHeight);

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add("shopify-section-header-sticky");
        }

        this.predictiveSearch = this.querySelector("predictive-search");

        document.documentElement.setAttribute("data-header-status", "visible");

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);

        window.addEventListener("scroll", this.onScrollHandler, { passive: true });

        this.createObserver();
      }

      disconnectedCallback() {
        window.removeEventListener("scroll", this.onScrollHandler);
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        if (this._mediaQueryList) {
          this._mediaQueryList.removeEventListener("change", this._boundSetHeaderHeight);
          this._mediaQueryList = null;
        }
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
        if (this._revealDebounce) {
          clearTimeout(this._revealDebounce);
          this._revealDebounce = null;
        }
        if (this._headerActionRaf) {
          cancelAnimationFrame(this._headerActionRaf);
          this._headerActionRaf = 0;
          this._pendingHeaderAction = "";
        }
      }

      setHeaderHeight() {
        const headerInner = this.header && this.header.querySelector("header");
        if (!headerInner) return;
        const newHeight = `${headerInner.offsetHeight}px`;
        if (document.documentElement.style.getPropertyValue("--header-height") === newHeight)
          return;
        document.documentElement.style.setProperty("--header-height", newHeight);
      }

      createObserver() {
        this._observer = new IntersectionObserver((entries, observer) => {
          this.headerBounds = entries[0].intersectionRect;

          observer.disconnect();
          this._observer = null;
        });

        this._observer.observe(this.header);
      }

      onScroll() {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // Deadzone: track peak/valley scroll position and only flip direction
        // after scrolling 8px back from the extremum. Prevents inertial
        // micro-oscillations from toggling the header.
        if (this._confirmedDirection === "down") {
          if (scrollTop >= this._lastDirectionChangeY) {
            this._lastDirectionChangeY = scrollTop;
          } else if (
            this._lastDirectionChangeY - scrollTop >
            this._DIRECTION_DEADZONE
          ) {
            this._confirmedDirection = "up";
            this._lastDirectionChangeY = scrollTop;
            this._upFlipTime = performance.now();
            this._upFlipScrollTop = scrollTop;
          }
        } else {
          if (scrollTop <= this._lastDirectionChangeY) {
            this._lastDirectionChangeY = scrollTop;
          } else if (
            scrollTop - this._lastDirectionChangeY >
            this._DIRECTION_DEADZONE
          ) {
            this._confirmedDirection = "down";
            this._lastDirectionChangeY = scrollTop;
          }
        }

        const scrollDirection = this._confirmedDirection;
        const directionAttr = this._isMobileProductPage ? "up" : scrollDirection;

        if (this._lastDirectionAttr !== directionAttr) {
          this._lastDirectionAttr = directionAttr;
          document.documentElement.setAttribute(
            "data-scroll-direction",
            directionAttr,
          );
        }

        if (
          document.body.hasAttribute("scroll-y-off") ||
          (this.predictiveSearch && this.predictiveSearch.isOpen)
        )
          return;

        const headerPassed = scrollTop > this.headerBounds.bottom;
        const headerToTop = scrollTop <= this.headerBounds.top;

        this.preScrollTop = scrollTop;

        if (headerToTop) {
          if (this.preStatus !== 3) {
            this.preStatus = 3;
            this.header.classList.remove("scrolled-past-header");
            this.openCategoryMenu();
            if (!this.headerIsAlwaysSticky) this.scheduleHeaderAction("reset");
          }

          return;
        }

        if (scrollDirection === "down" && headerPassed) {
          if (this._revealDebounce) {
            clearTimeout(this._revealDebounce);
            this._revealDebounce = null;
          }
          if (this.preStatus !== 1) {
            this.preStatus = 1;
            this.header.classList.add("scrolled-past-header");
            this.closeCategoryMenu();
            if (!this.preventHide && !this.headerIsAlwaysSticky) {
              this.scheduleHeaderAction("hide");
            }
          }
        } else if (scrollDirection === "up" && headerPassed) {
          if (this.preStatus !== 2) {
            const upDuration = performance.now() - this._upFlipTime;
            const scrolledFurther = scrollTop <= this._upFlipScrollTop;

            if (upDuration >= this._MIN_UP_DURATION && scrolledFurther) {
              this.preStatus = 2;
              this.header.classList.add("scrolled-past-header");
              this.closeCategoryMenu();

              if (!this.preventReveal) {
                if (!this.headerIsAlwaysSticky) this.scheduleHeaderAction("reveal");
              } else {
                window.clearTimeout(this.isScrolling);
                this.isScrolling = setTimeout(() => {
                  this.preventReveal = false;
                }, 60);
                if (!this.headerIsAlwaysSticky) this.scheduleHeaderAction("hide");
              }
            } else if (!this._revealDebounce) {
              const remaining = Math.max(50, this._MIN_UP_DURATION - upDuration);
              this._revealDebounce = setTimeout(() => {
                this._revealDebounce = null;
                const curScroll = window.pageYOffset || document.documentElement.scrollTop;
                if (this._confirmedDirection === "up" && curScroll <= this._upFlipScrollTop) {
                  this._onScrollRaw();
                }
              }, remaining);
            }
          }
        } else {
          if (this._revealDebounce) {
            clearTimeout(this._revealDebounce);
            this._revealDebounce = null;
          }
          this.preStatus = 0;
        }
      }

      hide() {
        this.header.classList.add(
          "shopify-section-header-hidden",
          "shopify-section-header-sticky",
        );
        this.closeDropMenus();
        document.documentElement.setAttribute("data-header-status", "hidden");
      }

      reveal() {
        this.header.classList.add(
          "shopify-section-header-sticky",
          "header-reveal-animate",
        );
        this.header.classList.remove("shopify-section-header-hidden");
        document.documentElement.setAttribute("data-header-status", "visible");
      }

      reset() {
        this.header.classList.remove(
          "shopify-section-header-hidden",
          "shopify-section-header-sticky",
          "header-reveal-animate",
        );
        document.documentElement.setAttribute("data-header-status", "visible");
      }

      closeDropMenus() {
        this.dropMenus =
          this.dropMenus || this.header.querySelectorAll("drop-menu");
        this.dropMenus.forEach((dropMenu) => dropMenu.close());
      }

      openCategoryMenu() {
        if (
          this.categoryMenu &&
          this.categoryMenu.hasAttribute("data-reveal-open")
        )
          this.categoryMenu.open();
      }

      closeCategoryMenu() {
        if (this.categoryMenu) this.categoryMenu.close();
      }
    },
  );
}
